import i18n from "@/core/plugins/vue-i18n";
import chatBotApiService from "@/components/ChatBot/services/chatBot.service.js";

const state = {
  messages: [],
  isLoading: false,
  isStreaming: false,
  conversationId: null,
  currentStreamingMessage: null,
  error: null,
  abortController: null
};

const mutations = {
  SET_MESSAGES(state, messages) {
    state.messages = messages;
  },
  ADD_MESSAGE(state, message) {
    state.messages.push(message);
  },
  SET_LOADING(state, isLoading) {
    state.isLoading = isLoading;
  },
  SET_STREAMING(state, isStreaming) {
    state.isStreaming = isStreaming;
  },
  SET_CONVERSATION_ID(state, conversationId) {
    state.conversationId = conversationId;
  },
  SET_CURRENT_STREAMING_MESSAGE(state, message) {
    state.currentStreamingMessage = message;
  },
  UPDATE_STREAMING_MESSAGE(state, chunk) {
    if (state.currentStreamingMessage) {
      state.currentStreamingMessage.text += chunk;
    }
  },
  FINISH_STREAMING_MESSAGE(state) {
    if (state.currentStreamingMessage) {
      state.currentStreamingMessage.isComplete = true;
      state.currentStreamingMessage = null;
    }
  },
  SET_ERROR(state, error) {
    state.error = error;
  },
  CLEAR_MESSAGES(state) {
    state.messages = [];
    state.currentStreamingMessage = null;
    state.error = null;
  },
  CLEAR_LOADING_VARIABLES(state) {
    state.isLoading = false;
    state.isStreaming = false;
  },
  SET_ABORT_CONTROLLER(state, abortController) {
    state.abortController = abortController;
  }
};

const actions = {
  initializeConversation({ commit }) {
    const conversationId = chatBotApiService.generateConversationId();
    commit("SET_CONVERSATION_ID", conversationId);

    const welcomeMessage = {
      id: 1,
      text: i18n.t("chatBot.welcomeMessage"),
      sender: "bot",
      isComplete: true
    };

    commit("SET_MESSAGES", [welcomeMessage]);
    commit("SET_ERROR", null);
  },

  addMessage({ commit }, { text, sender, messageId = null, isHtml = false }) {
    const message = {
      id: messageId || Date.now() + Math.random(),
      text,
      sender,
      timestamp: new Date().toLocaleTimeString("de-DE", {
        hour: "2-digit",
        minute: "2-digit"
      }),
      isComplete: true,
      isHtml
    };

    commit("ADD_MESSAGE", message);
    return message;
  },

  startStreamingMessage({ commit }) {
    const streamingMessage = {
      id: Date.now() + Math.random(),
      text: "",
      sender: "bot",
      timestamp: new Date().toLocaleTimeString("de-DE", {
        hour: "2-digit",
        minute: "2-digit"
      }),
      isComplete: false,
      isHtml: true
    };

    commit("ADD_MESSAGE", streamingMessage);
    commit("SET_CURRENT_STREAMING_MESSAGE", streamingMessage);
    return streamingMessage;
  },

  updateStreamingMessage({ commit, dispatch }, { chunk, aborted = false }) {
    if (aborted) {
      if (
        state.currentStreamingMessage &&
        state.currentStreamingMessage.text.length <= 0
      ) {
        state.currentStreamingMessage.text = i18n.t("chatBot.abortedResponse");
      }
      dispatch("finishStreamingMessage");
      return;
    }
    commit("UPDATE_STREAMING_MESSAGE", chunk);
  },

  finishStreamingMessage({ commit }) {
    commit("FINISH_STREAMING_MESSAGE");
  },

  async sendMessage(
    { commit, dispatch, state },
    { inputText, abortController = null }
  ) {
    if (!inputText.trim() || state.isLoading || state.isStreaming) {
      return;
    }

    try {
      commit("SET_ERROR", null);
      commit("SET_LOADING", true);

      // User-message hinzufügen
      dispatch("addMessage", { text: inputText, sender: "user" });

      // Generate conversation ID if not exists
      if (!state.conversationId) {
        const conversationId = chatBotApiService.generateConversationId();
        commit("SET_CONVERSATION_ID", conversationId);
      }

      // Start streaming a bot message
      commit("SET_STREAMING", true);
      dispatch("startStreamingMessage");

      await chatBotApiService.sendMessage({
        inputText,
        conversationId: state.conversationId,
        abortController: abortController ?? state.abortController,
        onChunk: batch => {
          const combinedText = batch.map(item => item.delta || "").join("");

          if (combinedText) {
            commit("SET_LOADING", false);
            dispatch("updateStreamingMessage", { chunk: combinedText });
          }
        },
        onComplete: () => {
          dispatch("finishStreamingMessage");
          commit("CLEAR_LOADING_VARIABLES");
        },
        onError: apiError => {
          const errorMessage =
            apiError.message || i18n.t("chatBot.errorMessageSend");
          commit("SET_ERROR", errorMessage);

          if (state.currentStreamingMessage) {
            state.currentStreamingMessage.text = i18n.t("chatBot.errorMessage");
            dispatch("finishStreamingMessage");
          }

          commit("CLEAR_LOADING_VARIABLES");
        }
      });
    } catch (err) {
      const errorMessage =
        err.message || i18n.t("chatBot.errorMessageUnexpectedError");
      commit("SET_ERROR", errorMessage);
      commit("CLEAR_LOADING_VARIABLES");

      dispatch("addMessage", {
        text: i18n.t("chatBot.errorMessage"),
        sender: "bot"
      });
    }
  },

  clearMessages({ commit }) {
    commit("CLEAR_MESSAGES");
  },

  clearLoadingVariables({ commit }) {
    commit("CLEAR_LOADING_VARIABLES");
  },

  startNewConversation({ dispatch }) {
    dispatch("clearMessages");
    dispatch("initializeConversation");
  },

  setAbortController({ commit }, abortController) {
    commit("SET_ABORT_CONTROLLER", abortController);
  }
};

const getters = {
  messages: state => state.messages,
  isLoading: state => state.isLoading,
  isStreaming: state => state.isStreaming,
  conversationId: state => state.conversationId,
  error: state => state.error,
  currentStreamingMessage: state => state.currentStreamingMessage,
  abortController: state => state.abortController
};

export default {
  state,
  mutations,
  actions,
  getters
};
